import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Box, font, basicRequiredValidation, useForm, Flex } from '@lemonade-hq/cdk';
import { TextArea, Button, MEDIA_QUERIES, BaseDialog, DialogHeader, DialogBody, DialogFooter, PrimaryInlineRoundIcon, InputText } from '@lemonade-hq/nuis';
import { isValidEmail, typedKeys } from '@lemonade-hq/ts-helpers';
import { useState, useMemo, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { ApiRoute } from '../../config/api.esm.js';
import { Client } from '../../helpers/client/client.esm.js';
import { useAnalyticsContext } from '../../providers/AnalyticsProvider.esm.js';
import { useCommonI18nContext } from '../../providers/I18nProvider.esm.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _tagged_template_literal(strings, raw) {
    if (!raw) {
        raw = strings.slice(0);
    }
    return Object.freeze(Object.defineProperties(strings, {
        raw: {
            value: Object.freeze(raw)
        }
    }));
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return(g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g);
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
function _templateObject() {
    var data = _tagged_template_literal([
        "\n  text-transform: uppercase;\n  text-align: center;\n  ",
        ";\n"
    ]);
    _templateObject = function _templateObject() {
        return data;
    };
    return data;
}
function _templateObject1() {
    var data = _tagged_template_literal([
        "\n  text-align: center;\n  ",
        ";\n"
    ]);
    _templateObject1 = function _templateObject1() {
        return data;
    };
    return data;
}
function _templateObject2() {
    var data = _tagged_template_literal([
        "\n  ",
        ";\n  text-align: center;\n"
    ]);
    _templateObject2 = function _templateObject2() {
        return data;
    };
    return data;
}
function _templateObject3() {
    var data = _tagged_template_literal([
        "\n  height: 83px;\n"
    ]);
    _templateObject3 = function _templateObject3() {
        return data;
    };
    return data;
}
function _templateObject4() {
    var data = _tagged_template_literal([
        "\n    width: 100%;\n  "
    ]);
    _templateObject4 = function _templateObject4() {
        return data;
    };
    return data;
}
function _templateObject5() {
    var data = _tagged_template_literal([
        "\n  ",
        "\n"
    ]);
    _templateObject5 = function _templateObject5() {
        return data;
    };
    return data;
}
function _templateObject6() {
    var data = _tagged_template_literal([
        "\n  ",
        ";\n  color: ",
        ";\n  position: relative;\n  text-align: center;\n  margin-bottom: 10px;\n  font-size: 14px;\n"
    ]);
    _templateObject6 = function _templateObject6() {
        return data;
    };
    return data;
}
var INPUT_DESKTOP_WIDTH = 355;
var MainTitle = styled(Box)(_templateObject(), font("main", {
    fontSize: "1.6rem",
    lineHeight: "1.5",
    fontWeight: 700
}));
var ThankYouTitle = styled(Box)(_templateObject1(), font("alt", {
    fontSize: "3.6rem",
    lineHeight: "1.3"
}));
var SubTitle = styled(Box)(_templateObject2(), font("main", {
    fontSize: "1.6rem",
    lineHeight: "1.3"
}));
var QuestionInput = styled(TextArea)(_templateObject3());
var PinkButton = styled(Button).attrs({
    height: 47,
    minWidth: 157,
    hugging: false,
    flavor: "primary"
})(_templateObject5(), MEDIA_QUERIES.mobile(_templateObject4()));
var ErrorContainer = styled(Box)(_templateObject6(), font("main", {
    fontSize: "1.6rem",
    lineHeight: "1.5"
}), function(param) {
    var theme = param.theme;
    return theme.colors.primary;
});
var isLengthGreaterThanEqual = function(length) {
    return function(string) {
        return string.length >= length;
    };
};
var AuADialog = function(param) {
    var onSuccess = param.onSuccess, onClose = param.onClose, user = param.user, _param_endpoint = param.endpoint, endpoint = _param_endpoint === void 0 ? ApiRoute.AskUsAnything : _param_endpoint, _param_prefilledName = param.prefilledName, prefilledName = _param_prefilledName === void 0 ? "" : _param_prefilledName, _param_prefilledEmail = param.prefilledEmail, prefilledEmail = _param_prefilledEmail === void 0 ? "" : _param_prefilledEmail, _param_prefilledQuestion = param.prefilledQuestion, prefilledQuestion = _param_prefilledQuestion === void 0 ? "" : _param_prefilledQuestion, trackingParams = param.trackingParams;
    var s = useCommonI18nContext().s;
    var _useState = _sliced_to_array(useState({
        thankYou: false,
        loading: false,
        submitted: false,
        serverError: ""
    }), 2), _useState_ = _useState[0], thankYou = _useState_.thankYou, submitted = _useState_.submitted, loading = _useState_.loading, serverError = _useState_.serverError, setAuaState = _useState[1];
    var trackEvent = useAnalyticsContext().trackEvent;
    var initialName = user ? [
        user.givenName,
        user.familyName
    ].join(" ") : prefilledName;
    var _user_email;
    var formConfig = {
        fields: {
            name: {
                startValue: initialName,
                validations: {
                    required: basicRequiredValidation,
                    greaterThan: {
                        errorMessage: s("contact_us.error.minimum_two_chars"),
                        test: isLengthGreaterThanEqual(2)
                    }
                }
            },
            email: {
                startValue: (_user_email = user === null || user === void 0 ? void 0 : user.email) !== null && _user_email !== void 0 ? _user_email : prefilledEmail,
                validations: {
                    required: basicRequiredValidation,
                    validEmail: {
                        errorMessage: s("contact_us.error.email"),
                        test: isValidEmail
                    }
                }
            },
            question: {
                startValue: prefilledQuestion,
                validations: {
                    required: basicRequiredValidation,
                    atLeastTwo: {
                        errorMessage: s("contact_us.error.minimum_two_chars"),
                        test: isLengthGreaterThanEqual(2)
                    }
                }
            }
        }
    };
    var _useForm = useForm(formConfig), valid = _useForm.valid, errors = _useForm.errors, setValue = _useForm.setValue, values = _useForm.values, enabled = _useForm.enabled, reset = _useForm.reset;
    var displayedErrors = typedKeys(errors).reduce(function(result, fieldName) {
        var _errors_fieldName;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return _to_consumable_array(result).concat(_to_consumable_array(Object.values((_errors_fieldName = errors[fieldName]) !== null && _errors_fieldName !== void 0 ? _errors_fieldName : [])));
    }, []);
    var displayedError = useMemo(function() {
        return displayedErrors.find(function(val) {
            return val.length > 0;
        });
    }, [
        displayedErrors
    ]);
    useEffect(function() {
        setAuaState(function(val) {
            return _object_spread_props(_object_spread({}, val), {
                submitted: false
            });
        });
    }, [
        setAuaState,
        values
    ]);
    var submitAua = useCallback(function() {
        var _ref = _async_to_generator(function(e) {
            var body;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        e.persist();
                        setAuaState(function(val) {
                            return _object_spread_props(_object_spread({}, val), {
                                submitted: true
                            });
                        });
                        if (!valid) return [
                            3,
                            5
                        ];
                        trackEvent("ask_us_anything_ticket_sent", trackingParams);
                        setAuaState(function(val) {
                            return _object_spread_props(_object_spread({}, val), {
                                loading: true
                            });
                        });
                        _state.label = 1;
                    case 1:
                        _state.trys.push([
                            1,
                            3,
                            4,
                            5
                        ]);
                        body = _object_spread({}, values);
                        return [
                            4,
                            Client.post(endpoint, body, {
                                withCredentials: true
                            })
                        ];
                    case 2:
                        _state.sent();
                        onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
                        setAuaState(function(val) {
                            return _object_spread_props(_object_spread({}, val), {
                                loading: false,
                                thankYou: true,
                                submitted: false
                            });
                        });
                        return [
                            3,
                            5
                        ];
                    case 3:
                        _state.sent();
                        setAuaState(function(val) {
                            return _object_spread_props(_object_spread({}, val), {
                                loading: false,
                                submitted: true,
                                serverError: s("contact_us.error.general")
                            });
                        });
                        return [
                            3,
                            5
                        ];
                    case 4:
                        /* istanbul ignore next */ // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                        if (e.currentTarget != null) e.currentTarget.blur();
                        return [
                            7
                        ];
                    case 5:
                        return [
                            2
                        ];
                }
            });
        });
        return function(e) {
            return _ref.apply(this, arguments);
        };
    }(), [
        endpoint,
        onSuccess,
        s,
        trackEvent,
        trackingParams,
        valid,
        values
    ]);
    var setValueOnChange = function(keyName) {
        return function(e) {
            setAuaState(function(val) {
                return _object_spread_props(_object_spread({}, val), {
                    serverError: ""
                });
            });
            setValue(keyName, e.target.value);
        };
    };
    var close = useCallback(function() {
        reset();
        setAuaState({
            thankYou: false,
            loading: false,
            submitted: false,
            serverError: ""
        });
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [
        onClose,
        reset
    ]);
    return /*#__PURE__*/ jsx(BaseDialog, {
        onClose: close,
        children: thankYou ? /*#__PURE__*/ jsxs(Fragment, {
            children: [
                /*#__PURE__*/ jsx(DialogHeader, {
                    children: /*#__PURE__*/ jsx(ThankYouTitle, {
                        "aria-role": "heading",
                        as: "h4",
                        children: s("contact_us.success.title", {
                            name: values.name
                        })
                    })
                }),
                /*#__PURE__*/ jsx(DialogBody, {
                    children: /*#__PURE__*/ jsx(SubTitle, {
                        as: "p",
                        pb: [
                            "20px",
                            "40px"
                        ],
                        pt: [
                            "10px",
                            "20px"
                        ],
                        px: [
                            "0",
                            "25px"
                        ],
                        children: s("contact_us.success.subtitle")
                    })
                }),
                /*#__PURE__*/ jsx(DialogFooter, {
                    pt: "30px",
                    children: /*#__PURE__*/ jsx(PinkButton, {
                        "aria-label": "ok",
                        glowing: true,
                        onClick: close,
                        children: s("contact_us.success.ok")
                    })
                })
            ]
        }) : /*#__PURE__*/ jsxs(Fragment, {
            children: [
                /*#__PURE__*/ jsx(DialogHeader, {
                    children: /*#__PURE__*/ jsx(MainTitle, {
                        as: "h4",
                        children: s("contact_us.title")
                    })
                }),
                /*#__PURE__*/ jsx(DialogBody, {
                    children: /*#__PURE__*/ jsxs(Flex, {
                        flexDirection: "column",
                        width: [
                            "297px",
                            "500px"
                        ],
                        children: [
                            /*#__PURE__*/ jsx(SubTitle, {
                                as: "p",
                                pb: [
                                    "20px",
                                    "40px"
                                ],
                                pt: [
                                    "10px",
                                    "20px"
                                ],
                                px: [
                                    "0",
                                    "20px"
                                ],
                                children: s("contact_us.subtitle")
                            }),
                            submitted && (displayedError != null || serverError.length > 0) && /*#__PURE__*/ jsxs(ErrorContainer, {
                                "aria-label": "error",
                                children: [
                                    /*#__PURE__*/ jsx(PrimaryInlineRoundIcon, {
                                        children: "!"
                                    }),
                                    "\xa0\xa0",
                                    "".concat(displayedError !== null && displayedError !== void 0 ? displayedError : serverError)
                                ]
                            }),
                            /*#__PURE__*/ jsxs(Flex, {
                                alignItems: "center",
                                "aria-label": "ask us anything form",
                                as: "form",
                                autoComplete: "off",
                                flexDirection: "column",
                                width: [
                                    1,
                                    "auto"
                                ],
                                children: [
                                    /*#__PURE__*/ jsx(Box, {
                                        width: [
                                            1,
                                            INPUT_DESKTOP_WIDTH
                                        ],
                                        children: /*#__PURE__*/ jsx(InputText, {
                                            highlightError: errors.name != null && submitted,
                                            name: "name",
                                            onChange: setValueOnChange("name"),
                                            placeholder: s("contact_us.placeholders.name"),
                                            value: values.name
                                        })
                                    }),
                                    /*#__PURE__*/ jsx(Box, {
                                        mt: "10px",
                                        width: [
                                            1,
                                            INPUT_DESKTOP_WIDTH
                                        ],
                                        children: /*#__PURE__*/ jsx(InputText, {
                                            highlightError: errors.email != null && submitted,
                                            name: "email",
                                            onChange: setValueOnChange("email"),
                                            placeholder: s("contact_us.placeholders.email"),
                                            value: values.email
                                        })
                                    }),
                                    /*#__PURE__*/ jsx(Box, {
                                        mt: "10px",
                                        width: [
                                            1,
                                            INPUT_DESKTOP_WIDTH
                                        ],
                                        children: /*#__PURE__*/ jsx(QuestionInput, {
                                            highlightError: errors.question != null && submitted,
                                            name: "text",
                                            onChange: setValueOnChange("question"),
                                            placeholder: s("contact_us.placeholders.text"),
                                            style: {
                                                resize: "none",
                                                overflow: "auto",
                                                borderRadius: 5
                                            },
                                            value: values.question
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ jsx(DialogFooter, {
                    pt: "30px",
                    children: /*#__PURE__*/ jsx(PinkButton, {
                        "aria-label": "submit",
                        disabled: !enabled,
                        glowing: true,
                        isLoading: valid && loading && !thankYou,
                        onClick: function(e) {
                            void submitAua(e);
                        },
                        children: s("contact_us.send")
                    })
                })
            ]
        })
    });
};

export { AuADialog };
